import React, {
    useState,
    forwardRef,
    useImperativeHandle,
    useEffect,
    ForwardedRef,
} from "react";
import { Card, Checkbox, Input } from "../../../../../shared/custom-ui";

interface MRIFormProps {
    defaultValues?: Record<string, any>;
}

const MRIForm = forwardRef((props: MRIFormProps, ref: ForwardedRef<any>) => {
    const { defaultValues = {} } = props;
    const [mriObj, setMriObj] = useState<Record<string, any>>({});
    const [isPrefilled, setIsPrefilled] = useState(false);

    useEffect(() => {
        if (!isPrefilled && Object.keys(defaultValues).length > 0) {
            setMriObj(prev => ({ ...prev, ...defaultValues }));
            setIsPrefilled(true);
        }
    }, [defaultValues, isPrefilled]);

    useImperativeHandle(ref, () => ({
        getData: () => mriObj,
    }));

    const handleCheckboxChange = (key: string) => {
        setMriObj(prev => {
            if (prev[key]) {
                const { [key]: _, ...rest } = prev;
                return rest;
            }
            return { ...prev, [key]: true };
        });
    };

    const handleInputChange = (key: string, value: string | null) => {
        setMriObj(prev => {
            if (value) {
                return { ...prev, [key]: value };
            } else {
                const { [key]: _, ...rest } = prev;
                return rest;
            }
        });
    };

    return (
        <Card title="MRI" padding="small">
            <div className="space-y-6 p-4">
                {/* Contrast Section */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Checkbox
                        label="Contrast as determined by radiologist"
                        checked={mriObj.contrast_radiologist || false}
                        onChange={() => handleCheckboxChange("contrast_radiologist")}
                    />
                    <Checkbox
                        label="Without contrast"
                        checked={mriObj.without_contrast || false}
                        onChange={() => handleCheckboxChange("without_contrast")}
                    />
                    <Checkbox
                        label="With contrast"
                        checked={mriObj.with_contrast || false}
                        onChange={() => handleCheckboxChange("with_contrast")}
                    />
                    <Checkbox
                        label="Without & with contrast"
                        checked={mriObj.without_with_contrast || false}
                        onChange={() => handleCheckboxChange("without_with_contrast")}
                    />
                    <Checkbox
                        label="Draw Creatinine labs if medically indicated for contrast delivery"
                        checked={mriObj.creatinine_labs || false}
                        onChange={() => handleCheckboxChange("creatinine_labs")}
                    />
                </div>
                {/* Head/Neck Section */}
                <h3 className="text-lg font-semibold">Head/Neck</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        <Checkbox
                            label="MRI Brain"
                            checked={mriObj.mri_brain || false}
                            onChange={() => handleCheckboxChange("mri_brain")}
                        />
                        <Checkbox
                            label="Pituitary"
                            checked={mriObj.pituitary || false}
                            onChange={() => handleCheckboxChange("pituitary")}
                        />
                        <Checkbox
                            label="Orbits"
                            checked={mriObj.orbits || false}
                            onChange={() => handleCheckboxChange("orbits")}
                        />
                        <Checkbox
                            label="IACs"
                            checked={mriObj.iacs || false}
                            onChange={() => handleCheckboxChange("iacs")}
                        />
                    </div>
                    <div className="grid grid-cols-2 items-center">
                        <Checkbox
                            label="MRI Brain"
                            checked={mriObj.mri_brain_specify || false}
                            onChange={() => handleCheckboxChange("mri_brain_specify")}
                        />
                        <Input
                            label="Specify"
                            value={mriObj.mri_brain_specify_text || ""}
                            onChange={(e) => handleInputChange("mri_brain_specify_text", e.target.value)}
                        />
                    </div>
                    <Checkbox
                        label="MRA brain"
                        checked={mriObj.mra_brain || false}
                        onChange={() => handleCheckboxChange("mra_brain")}
                    />
                    <Checkbox
                        label="MRA neck"
                        checked={mriObj.mra_neck || false}
                        onChange={() => handleCheckboxChange("mra_neck")}
                    />
                    <Checkbox
                        label="MRV Head"
                        checked={mriObj.mrv_head || false}
                        onChange={() => handleCheckboxChange("mrv_head")}
                    />
                </div>

                {/* Spine Section */}
                <h3 className="text-lg font-semibold">Spine</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Checkbox
                        label="Cervical"
                        checked={mriObj.spine_cervical || false}
                        onChange={() => handleCheckboxChange("spine_cervical")}
                    />
                    <Checkbox
                        label="Thoracic"
                        checked={mriObj.spine_thoracic || false}
                        onChange={() => handleCheckboxChange("spine_thoracic")}
                    />
                    <Checkbox
                        label="Lumbar"
                        checked={mriObj.spine_lumbar || false}
                        onChange={() => handleCheckboxChange("spine_lumbar")}
                    />
                </div>

                {/* Extremities Section */}
                <h2 className="text-lg font-semibold">Extremities</h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 mt-4 gap-4 gap-x-8">
                    <div className="flex justify-between items-center space-x-3">
                        <div className="flex space-x-4">
                            <Checkbox
                                label="Shoulder"
                                checked={mriObj.shoulder || false}
                                onChange={() => handleCheckboxChange("shoulder")}
                            />
                            <Checkbox
                                label="With MR Arthrogram"
                                checked={mriObj.shoulder_arthrogram || false}
                                onChange={() => handleCheckboxChange("shoulder_arthrogram")}
                            />
                        </div>
                        <div className="flex space-x-4">
                            <Checkbox
                                label="L"
                                checked={mriObj.shoulder_left || false}
                                onChange={() => handleCheckboxChange("shoulder_left")}
                            />
                            <Checkbox
                                label="R"
                                checked={mriObj.shoulder_right || false}
                                onChange={() => handleCheckboxChange("shoulder_right")}
                            />
                        </div>
                    </div>
                    {/* Repeat for other body parts similarly */}
                </div>

                {/* Chest Section */}
                <h2 className="text-lg font-semibold">Chest</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex">
                        <Checkbox
                            label="MRI Chest (specify site)"
                            checked={mriObj.mri_chest || false}
                            onChange={() => handleCheckboxChange("mri_chest")}
                        />
                        <Input
                            label="Specify site"
                            className="ml-5"
                            value={mriObj.mri_chest_site || ""}
                            onChange={(e) => handleInputChange("mri_chest_site", e.target.value)}
                        />
                    </div>
                    <Checkbox
                        label="MRA Chest (thoracic aorta)"
                        checked={mriObj.mra_chest || false}
                        onChange={() => handleCheckboxChange("mra_chest")}
                    />
                    <Checkbox
                        label="MRI Brachial plexus"
                        checked={mriObj.mri_brachial_plexus || false}
                        onChange={() => handleCheckboxChange("mri_brachial_plexus")}
                    />
                </div>

                {/* Abdomen Section */}
                <h2 className="text-lg font-semibold">Abdomen</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <Checkbox
                        label="MRI Liver"
                        checked={mriObj.mri_liver || false}
                        onChange={() => handleCheckboxChange("mri_liver")}
                    />
                    <Checkbox
                        label="MRI Kidney/renal"
                        checked={mriObj.mri_kidney || false}
                        onChange={() => handleCheckboxChange("mri_kidney")}
                    />
                    <Checkbox
                        label="MRI Pancreas/MRCP"
                        checked={mriObj.mri_pancreas || false}
                        onChange={() => handleCheckboxChange("mri_pancreas")}
                    />
                    <div className="flex">
                        <Checkbox
                            label="MRI Abdomen (specify organ)"
                            checked={mriObj.mri_abdomen || false}
                            onChange={() => handleCheckboxChange("mri_abdomen")}
                        />
                        <Input
                            placeholder="Specify organ"
                            className="ml-5"
                            value={mriObj.mri_abdomen_organ || ""}
                            onChange={(e) => handleInputChange("mri_abdomen_organ", e.target.value)}
                        />
                    </div>
                    <Checkbox
                        label="MRA Abdomen-aorta"
                        checked={mriObj.mra_abdomen_aorta || false}
                        onChange={() => handleCheckboxChange("mra_abdomen_aorta")}
                    />
                    <Checkbox
                        label="MRA Abdomen-mesenteric"
                        checked={mriObj.mra_abdomen_mesenteric || false}
                        onChange={() => handleCheckboxChange("mra_abdomen_mesenteric")}
                    />
                    <Checkbox
                        label="MRA Renal"
                        checked={mriObj.mra_renal || false}
                        onChange={() => handleCheckboxChange("mra_renal")}
                    />
                </div>

                {/* Pelvis Section */}
                <h2 className="text-lg font-semibold">Pelvis</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div className="flex">
                        <Checkbox
                            label="Bony"
                            checked={mriObj.pelvis_bony || false}
                            onChange={() => handleCheckboxChange("pelvis_bony")}
                        />
                        <Checkbox
                            label="SI joints"
                            className="ml-5"
                            checked={mriObj.pelvis_si_joints || false}
                            onChange={() => handleCheckboxChange("pelvis_si_joints")}
                        />
                    </div>
                    <div className="flex space-x-5">
                        <Checkbox
                            label="Soft tissue"
                            checked={mriObj.pelvis_soft_tissue || false}
                            onChange={() => handleCheckboxChange("pelvis_soft_tissue")}
                        />
                        <Checkbox
                            label="Uterus/ovaries"
                            checked={mriObj.pelvis_uterus_ovaries || false}
                            onChange={() => handleCheckboxChange("pelvis_uterus_ovaries")}
                        />
                        <Checkbox
                            label="Prostate"
                            checked={mriObj.pelvis_prostate || false}
                            onChange={() => handleCheckboxChange("pelvis_prostate")}
                        />
                        <Checkbox
                            label="Rectal"
                            checked={mriObj.pelvis_rectal || false}
                            onChange={() => handleCheckboxChange("pelvis_rectal")}
                        />
                    </div>

                    <div className="flex">
                        <Checkbox
                            label="Soft tissue (specify site)"
                            checked={mriObj.pelvis_soft_tissue_specify || false}
                            onChange={() => handleCheckboxChange("pelvis_soft_tissue_specify")}
                        />
                        <Input
                            placeholder="Specify site"
                            className="ml-4"
                            value={mriObj.pelvis_soft_tissue_specify_text || ""}
                            onChange={(e) => handleInputChange("pelvis_soft_tissue_specify_text", e.target.value)}
                        />
                    </div>
                    <Checkbox
                        label="MRV Pelvis"
                        checked={mriObj.mrv_pelvis || false}
                        onChange={() => handleCheckboxChange("mrv_pelvis")}
                    />
                    <Checkbox
                        label="Sacrum/Coccyx"
                        checked={mriObj.sacrum_coccyx || false}
                        onChange={() => handleCheckboxChange("sacrum_coccyx")}
                    />
                </div>

                <h2 className="text-lg font-semibold">Abdomen/Pelvis</h2>
                <Checkbox
                    label="Enterography (MRE)"
                    className="mt-3"
                    checked={mriObj.enterography || false}
                    onChange={() => handleCheckboxChange("enterography")}
                />

                <h2 className="text-lg font-semibold  mb-4">Peripheral</h2>
                <Checkbox
                    label="MRA peripheral"
                    checked={mriObj.mra_peripheral || false}
                    onChange={() => handleCheckboxChange("mra_peripheral")}
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Checkbox
                        label="Other MRI/MRA/MRV"
                        checked={mriObj.other_mri || false}
                        onChange={() => handleCheckboxChange("other_mri")}
                    />
                    <Input
                        placeholder="Specify other"
                        value={mriObj.other_mri_specify || ""}
                        onChange={(e) => handleInputChange("other_mri_specify", e.target.value)}
                    />
                </div>
            </div>
        </Card>
    );
});

export default MRIForm;
